import React, { useState, useCallback, useMemo } from 'react';

import styles from './Table.module.css';

import { TableColCell } from './TableColCell/TableColCell';
import { TableCell } from './TableCell/TableCell';

import { Column } from '../../../interfaces/Columns';
import { HasID, IColumnOrder } from '../../../interfaces/UtilInterfaces';

interface TableProps<T> {
    columns: Column<T>[],
    rows: T[],
    defaultColumnToOrder?: [IColumnOrder, React.Dispatch<React.SetStateAction<IColumnOrder>>],
    activeCells?: { [key: number]: number }
    setColorRow?: (row: T) => string | undefined,
    onClickRow?: (id: number) => void,
    onClickCell?: (idRow: number, idCell: number) => void,
    nohoverRow?: true
    activeRowID?: number | null
    preventSort?: true
}

const Table = <T extends HasID>({
    columns, rows, defaultColumnToOrder, activeCells, setColorRow,
    onClickRow, onClickCell, nohoverRow, activeRowID, preventSort }: TableProps<T>) => {

    const [columnToOrderBy, setColumnToOrderBy] = useState<number>(defaultColumnToOrder ? defaultColumnToOrder[0].col || 0 : 0);
    const [isOrderAsc, setIsOrderAsc] = useState<boolean>(defaultColumnToOrder ? defaultColumnToOrder[0].asc : true);

    const visibleColumns = useMemo(() => {
        return columns.filter(col => col.visible);
    }, [columns]);

    const handleClickColumnHeader = (index: number) => {
        if (defaultColumnToOrder) defaultColumnToOrder[1]({ col: index, asc: !isOrderAsc });
        setIsOrderAsc(oldIsOrderAsc => !oldIsOrderAsc);
        setColumnToOrderBy(index);
    };

    const returnCellValue = useCallback((a: T) => {
        const column = visibleColumns[columnToOrderBy];
        if (column?.cell?.(a)) return column.cell(a);
        if (column?.sortElementFunc) return column.sortElementFunc(a);
        return '';
    }, [columnToOrderBy, visibleColumns]);

    const compareValues = useCallback((a: T, b: T) => {
        if (Number(returnCellValue(a))) return +returnCellValue(a) - +returnCellValue(b);
        else return returnCellValue(a).localeCompare(returnCellValue(b));
    }, [returnCellValue]);

    const orderedRows = useMemo(() => {
        if (preventSort) return rows;
        const sortedRows = [...rows];
        if (isOrderAsc) return sortedRows.sort((a, b) => compareValues(a, b));
        return sortedRows.sort((a, b) => compareValues(b, a));
    }, [compareValues, isOrderAsc, preventSort, rows]);

    const handleClickRow = (row: T) => {
        if (onClickRow && row.id !== undefined) onClickRow(row.id);
    };

    const handleClickCell = (column: Column<T>, idRow?: number) => (e: React.MouseEvent<HTMLElement>) => {
        if (idRow === undefined || !column.selectableCell) return;
        e.stopPropagation();
        if (onClickCell) onClickCell(idRow, column.id);
    };

    const getActiveCell = (idCol: number, idRow?: number) => idRow !== undefined && activeCells?.[idRow] === idCol;


    return (
        <div className={styles.tableContainer}>
            <table cellSpacing="0" className={styles.table}>
                <thead>
                    <tr className={styles.headerTable}>
                        {visibleColumns.map((column, index) =>
                            <TableColCell
                                key={index}
                                col={column}
                                visible={index === columnToOrderBy}
                                isOrderAsc={isOrderAsc}
                                onClick={() => handleClickColumnHeader(index)}
                            />
                        )}
                    </tr>
                </thead>
                <tbody>
                    {orderedRows.map(row => {
                        return (
                            <tr
                                key={row.id}
                                className={nohoverRow ? styles.bodyRowNoHover : styles.bodyRow}
                                onClick={() => handleClickRow(row)}
                                style={{
                                    backgroundColor:
                                        activeRowID === row.id
                                            ? 'var(--highlighted-row)'
                                            : (setColorRow && setColorRow(row))
                                }}
                            >
                                {visibleColumns.map((col, index) =>
                                    <TableCell
                                        key={index}
                                        col={col}
                                        row={row}
                                        active={getActiveCell(col.id, row.id)}
                                        handleClick={handleClickCell(col, row.id)}
                                    />
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
export default Table;